import { useMemo, useState } from 'react';
import { libelleMetier } from '../../config/servicesCatalog';
import { useAuthContext } from '../../context/AuthContext';
import { useCreateProspect } from '../../hooks/useProspects';
import {
  METIERS_MANNUELS,
  TELEPHONE_REGEX,
  construireProspectManuel,
  normaliserTelephone,
  trouverDoublonTelephone
} from '../../utils/prospectImport';

/**
 * Modale « ➕ Nouveau prospect » (étape 1, §5.2 bis).
 *
 * Trois garde-fous, tous calculés par les **fonctions pures** de `prospectImport` (aucune règle
 * dupliquée ici) :
 *  1. **doublon par téléphone normalisé** — le `placeId` manuel est un horodatage, il ne peut pas
 *     révéler un doublon : le bouton reste bloqué et la fiche existante est nommée ;
 *  2. nom et téléphone obligatoires ;
 *  3. le statut initial n'est pas choisi : il découle du numéro (`exclu` si invalide) et de
 *     l'adresse (`a_completer` si incomplète, `a_appeler` sinon) — exactement la règle de l'import.
 *
 * Rien n'est inventé sur l'entreprise : `categories: []`, `google.*` à `null`, `activitySummary`
 * et `email` à `null`, `services: []`.
 *
 * Le champ **Métier est libre** (30/10/2026) : un `<datalist>` propose les trois métiers du run de
 * référence, mais la saisie n'est pas contrainte. La canonisation (« Plombier » → `plomberie`) et le
 * plafond de 10 points restent dans `prospectImport` / `servicesCatalog`, jamais ici.
 */

const ETATS_INITIAUX = {
  name: '',
  phone: '',
  street: '',
  postalCode: '',
  city: '',
  targetTrade: ''
};

const CreateProspectModal = ({ existants = [], onFermer, onCree }) => {
  const { currentUser } = useAuthContext();
  const { creer, enCours, erreur } = useCreateProspect();

  const [formulaire, setFormulaire] = useState(ETATS_INITIAUX);

  const changer = (cle, valeur) => setFormulaire((ancien) => ({ ...ancien, [cle]: valeur }));

  const telephoneNormalise = normaliserTelephone(formulaire.phone);
  const telephoneValide = telephoneNormalise ? TELEPHONE_REGEX.test(telephoneNormalise) : false;
  const doublon = useMemo(
    () => trouverDoublonTelephone(existants, formulaire.phone),
    [existants, formulaire.phone]
  );

  const adresseComplete = Boolean(
    formulaire.street.trim().length >= 5 &&
      /^\d{5}$/.test(formulaire.postalCode.trim()) &&
      formulaire.city.trim()
  );

  const pret = Boolean(formulaire.name.trim() && telephoneNormalise && !doublon && !enCours);

  const statutPrevu = !telephoneNormalise
    ? null
    : !telephoneValide
    ? { libelle: 'Exclue (numéro invalide)', ton: 'badge-error' }
    : adresseComplete
    ? { libelle: 'À appeler', ton: 'badge-info' }
    : { libelle: 'À compléter', ton: 'badge-warning' };

  const soumettre = async (evenement) => {
    evenement.preventDefault();
    if (!pret) return;

    try {
      const document = construireProspectManuel(formulaire, { uid: currentUser?.uid || null });
      await creer({ document, uid: currentUser?.uid || null });
      onCree?.(document.placeId);
    } catch {
      // L'erreur d'écriture est déjà exposée par le hook (`erreur`) : rien à faire ici.
    }
  };

  return (
    <div className="modal modal-open">
      <form className="modal-box max-w-xl" onSubmit={soumettre}>
        <h3 className="font-bold text-lg mb-1">Nouveau prospect</h3>
        <p className="text-sm text-base-content/60 mb-4">
          Fiche sans site web repérée hors Apify. Seuls le nom et le téléphone sont obligatoires.
        </p>

        <div className="space-y-3">
          <label className="form-control w-full">
            <span className="label-text">Nom de l’entreprise *</span>
            <input
              type="text"
              className="input input-bordered input-sm"
              value={formulaire.name}
              onChange={(evenement) => changer('name', evenement.target.value)}
              placeholder="Plomberie Dupont"
              required
            />
          </label>

          <label className="form-control w-full">
            <span className="label-text">Téléphone *</span>
            <input
              type="tel"
              className="input input-bordered input-sm"
              value={formulaire.phone}
              onChange={(evenement) => changer('phone', evenement.target.value)}
              placeholder="06 12 34 56 78"
              required
            />
            {telephoneNormalise && !telephoneValide && (
              <span className="label-text-alt text-warning">
                Numéro non reconnu (attendu : 0XXXXXXXXX ou +33XXXXXXXXX) — la fiche sera créée en
                « Exclue ».
              </span>
            )}
            {doublon && (
              <span className="label-text-alt text-error">
                Doublon : « {doublon.name} » existe déjà avec ce numéro. Ouvrez la fiche existante
                au lieu d’en créer une seconde.
              </span>
            )}
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <label className="form-control">
              <span className="label-text">Rue</span>
              <input
                type="text"
                className="input input-bordered input-sm"
                value={formulaire.street}
                onChange={(evenement) => changer('street', evenement.target.value)}
                placeholder="12 Rue des Lilas"
              />
            </label>
            <label className="form-control">
              <span className="label-text">Code postal</span>
              <input
                type="text"
                inputMode="numeric"
                maxLength={5}
                className="input input-bordered input-sm"
                value={formulaire.postalCode}
                onChange={(evenement) => changer('postalCode', evenement.target.value)}
                placeholder="60000"
              />
            </label>
            <label className="form-control">
              <span className="label-text">Ville</span>
              <input
                type="text"
                className="input input-bordered input-sm"
                value={formulaire.city}
                onChange={(evenement) => changer('city', evenement.target.value)}
                placeholder="Beauvais"
              />
            </label>
          </div>

          <label className="form-control w-full max-w-xs">
            <span className="label-text">Métier</span>
            <input
              type="text"
              className="input input-bordered input-sm"
              list="metiers-suggeres"
              maxLength={60}
              value={formulaire.targetTrade}
              onChange={(evenement) => changer('targetTrade', evenement.target.value)}
              placeholder="Plomberie, chauffage, maçonnerie…"
            />
            {/* Suggestions, pas contrainte : le champ reste libre (D10). */}
            <datalist id="metiers-suggeres">
              {METIERS_MANNUELS.map((metier) => (
                <option key={metier} value={libelleMetier(metier)} />
              ))}
            </datalist>
            <span className="label-text-alt text-base-content/50">
              Champ libre. Un métier reconnu est ramené à son code interne ; les 10 points de métier
              restent réservés à la plomberie et au chauffage.
            </span>
          </label>

          {statutPrevu && (
            <p className="text-sm text-base-content/70">
              Statut initial calculé :{' '}
              <span className={`badge badge-sm ${statutPrevu.ton}`}>{statutPrevu.libelle}</span>
            </p>
          )}
        </div>

        {erreur && (
          <div className="alert alert-error text-sm mt-4">
            <span>
              {erreur.code === 'permission-denied'
                ? 'Accès refusé : déployez la règle `prospects` (firebase deploy --only firestore:rules).'
                : erreur.message}
            </span>
          </div>
        )}

        <div className="modal-action">
          <button type="button" className="btn btn-ghost btn-sm" onClick={onFermer}>
            Annuler
          </button>
          <button type="submit" className="btn btn-primary btn-sm" disabled={!pret}>
            {enCours ? 'Création…' : 'Créer la fiche'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateProspectModal;
